import { useId } from "@fluentui/react-components";
import { Avatar, Divider } from "@mui/material";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";
import { ContactActions } from "../helper/ContactActions";
import { IContactData, ICustomContactListProps, IPartnerContacts } from "./Interfaces/CustomContact.interfaces";
import { contactListStyles as styles } from "../components/CustomerView/ContactList.styles";
import ContactListShimmer from "../components/CustomerView/ContactListShimmer";
import { NO_DATA_MESSAGES } from "./Constants/CustomContactList.constants";

export const CustomContactList = ({ selectedKey, contactData }: ICustomContactListProps) => {
  const tableId = useId("contacts-list-container");
  const HandleAPIException = () => (contactData.error && <DisplayAPIsException height={250} />) || "";

  const getNameInitials = (name: string) => {
    const idx = name.lastIndexOf(" ");
    const _name: string = name.charAt(idx > 0 ? idx + 1 : idx);
    return _name.match(/^[a-z]/i) ? _name : "";
  };
  const getJobTitle = (name: string) => {
    const parts = name.split(" - ");
    return parts[1] || "";
  };
  const formatRoleHeading = (role: string) => {
    // Convert "PrimaryCSAM" to "Primary CSAM", "PrimaryCSAMManager" to "Primary CSAM Manager"
    return role.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/CSAM/g, "CSAM").toUpperCase();
  };
  const getNoDataMessage = () => {
    return NO_DATA_MESSAGES[selectedKey] ?? "";
  };

  const getAvatarAndFullName = (contact: IContactData) => {
    const displayName = selectedKey === "csamContacts" ? (contact.name || contact.fullName) : contact.fullName;
    return (
      <>
        {contact.imageSrc ? (
          <Avatar alt={displayName || ""} src={contact.imageSrc} />
        ) : (
          <Avatar sx={{ bgcolor: "var(--colorBrandBackgroundHover)" }} aria-label="contact" title={displayName}>
            {displayName?.charAt(0)}
            {getNameInitials(displayName || "")}
          </Avatar>
        )}
      </>
    );
  };

  const renderLabel = (value?: string) =>
    value ? (
      <>
        <label className={styles.contactJob} title={value}>
          {value}
        </label>
      </>
    ) : null;

  const getLabel = (contact: IPartnerContacts | IContactData) => {
    if (selectedKey === "partnerContacts") {
      const { primaryRoleType, managementLevel, primaryPdm } = contact as IPartnerContacts;
      return (
        <>
          {renderLabel(primaryRoleType)}
          {renderLabel(managementLevel)}
          {renderLabel(primaryPdm)}
        </>
      );
    }

    if (selectedKey === "csamContacts") {
      const { nameRole } = contact as IContactData;
      // Format "PrimaryCSAM" to "Primary CSAM"
      const formattedRole = nameRole?.replace(/([a-z])([A-Z])/g, "$1 $2") || "";
      return renderLabel(formattedRole);
    }

    const { nameRole } = contact as IContactData;
    return renderLabel(nameRole);
  };

  if (contactData.isLoading) {
    return (
      <>
        <ContactListShimmer />
        <ContactListShimmer />
        <ContactListShimmer />
        <ContactListShimmer />
        <br />
      </>
    );
  }

  if (contactData.error) {
    return <HandleAPIException />;
  }

  if (contactData.isSuccess && !contactData.data?.length) {
    return <div>{getNoDataMessage()}</div>;
  }

  return (
    <>
      <br />
      {contactData.data?.map(({ categoryName, data: d, title }, productIndex) => (
        <div key={`product-${productIndex}-${categoryName?.trim()}`}>
          <div className={styles.titleArea}></div>
          {selectedKey === "automatedContacts" && title ? (
            <label>{title || "(JOB TITLE EMPTY)"}</label>
          ) : selectedKey === "partnerContacts" ? (
            <label className={styles.titleArea}>{categoryName || "(SUBSIDIARY NAME EMPTY)"}</label>
          ) : selectedKey === "csamContacts" ? (
            <label className={styles.titleArea}>{formatRoleHeading(categoryName || "") || "(ROLE EMPTY)"}</label>
          ) : (
            <label className={styles.titleArea}>{categoryName || "(PRODUCT NAME EMPTY)"}</label>
          )}
          <Divider orientation="horizontal" />
          <div className={styles.divTag}></div>
          {d.map((contact, index) => (
            <table key={`${tableId}-${categoryName?.trim()}-${index}`} className={styles.tableBorder}>
              <tbody className={styles.tableBorder}>
                <tr className={styles.tableBorder}>
                  <td className={styles.tableBorder}>{getAvatarAndFullName(contact)}</td>
                  <td className={styles.tableBorder}>
                    <div className={styles.contactDiv}>
                      <div className={styles.contactName}>
                        <label title={selectedKey === "csamContacts" ? (contact.name || contact.fullName) : contact.fullName}>
                          {selectedKey === "csamContacts" ? (contact.name || contact.fullName) : contact.fullName}
                        </label>
                        <br />
                        {selectedKey === "automatedContacts" ? (
                          (() => {
                            const jobTitle = getJobTitle(contact.nameRole);
                            return (
                              <label className={styles.contactJob} title={jobTitle}>
                                {jobTitle}
                              </label>
                            );
                          })()
                        ) : (
                          <>{getLabel(contact)}</>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={styles.tableBorder}>
                    <ContactActions email={contact.emailAddress} />
                  </td>
                </tr>
              </tbody>
            </table>
          ))}
          <br />
        </div>
      ))}
    </>
  );
};
